/** Common fragments */
export type Money = { value?: number; currency?: string };

export type ContentItem = {
  id: string;
  quantity?: number;
  item_price?: number;
};

export type Contents =
  | { content_ids?: string[]; content_type?: string }
  | { contents?: ContentItem[]; content_type?: string };

export type ActionSource =
  | "email"
  | "website"
  | "phone_call"
  | "chat"
  | "physical_store"
  | "system_generated"
  | "other";

/** Standard event names */
export const FB_STANDARD_EVENTS = [
  "Purchase",
  "AddPaymentInfo",
  "AddToCart",
  "AddToWishlist",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "InitiateCheckout",
  "Lead",
  "PageView",
  "Schedule",
  "Search",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
  "ViewContent",
] as const;

export type FBStandardEvent = typeof FB_STANDARD_EVENTS[number];

/** Param shapes per event for CAPI */
export interface FBEventMap {
  PageView: Record<string, never>;
  ViewContent: Partial<Contents & Money> & {
    content_name?: string;
    content_category?: string;
  };
  Search: { search_string?: string } & Partial<Money>;
  AddToCart: Partial<Contents & Money>;
  AddToWishlist: Partial<Contents & Money>;
  InitiateCheckout: Partial<Contents & Money> & { num_items?: number };
  AddPaymentInfo: Partial<Contents & Money>;
  Purchase: Required<Money> & Partial<Contents> & { num_items?: number };
  Lead: { value?: number; currency?: string; content_name?: string };
  CompleteRegistration: { status?: boolean } & Partial<Money>;
  Contact: Record<string, never>;
  CustomizeProduct: Partial<Contents & Money>;
  Donate: Partial<Money>;
  FindLocation: Record<string, never>;
  Schedule: Record<string, never>;
  StartTrial: Partial<Money>;
  SubmitApplication: Record<string, never>;
  Subscribe: Partial<Money>;
}

/** User data fields for matching (hash on server when required) */
export interface UserData {
  em?: string; // SHA-256 hashed email
  ph?: string; // SHA-256 hashed phone
  ge?: string; // gender (f/m) hashed if PII
  db?: string; // date of birth YYYYMMDD (hashed if PII)
  ln?: string; // last name hashed
  fn?: string; // first name hashed
  ct?: string; // city hashed
  st?: string; // state hashed
  zp?: string; // zip hashed
  country?: string; // country hashed
  external_id?: string; // hashed
  fbp?: string; // First-party browser id from _fbp cookie
  fbc?: string; // Click id from _fbc cookie
  client_ip_address?: string;
  client_user_agent?: string;
}

/** The raw event payload we send inside "data" array */
export interface GraphEvent<E extends FBStandardEvent = FBStandardEvent> {
  event_name: E;
  event_time: number; // seconds since epoch
  action_source: ActionSource;
  event_id?: string;
  event_source_url?: string;
  user_data?: UserData;
  custom_data?: FBEventMap[E];
}

export interface SendOptions {
  pixelId: string;
  accessToken: string;
  events: GraphEvent[];
  testEventCode?: string;
  graphVersion?: string; // default v19.0
  debug?: boolean;
}

export interface SendResult {
  ok: boolean;
  status: number;
  body: any;
}
